/**
 * Utility functions for checking agency permissions and vehicle access rights
 * Extends the existing vehicle filtering system to include child agencies
 */

/**
 * Gets all child agency IDs recursively from a parent agency
 * @param {number} parentAgencyId - The parent agency ID
 * @param {Array} agenciesTree - The agencies tree structure
 * @returns {Array} Array of child agency IDs (not including the parent)
 */
export function getAllChildAgencyIds(parentAgencyId, agenciesTree) {
  const childIds = []

  function findAndAddChildren(agency) {
    if (agency.children && agency.children.length > 0) {
      for (const child of agency.children) {
        childIds.push(child.id)
        // Recursively add all descendants of this child
        findAndAddChildren(child)
      }
    }
  }

  function findParentAgency(agencies) {
    for (const agency of agencies) {
      if (agency.id === parentAgencyId) {
        findAndAddChildren(agency)
        return true
      }
      if (agency.children && agency.children.length > 0) {
        if (findParentAgency(agency.children)) {
          return true
        }
      }
    }
    return false
  }

  findParentAgency(agenciesTree)
  return [...new Set(childIds)] // Remove duplicates
}

/**
 * Gets all agency IDs that a user has access to (role agencies + their children)
 * @param {Array} userRoles - Array of user role agency IDs
 * @param {Array} agenciesTree - The agencies tree structure
 * @returns {Array} Array of all allowed agency IDs
 */
export function getUserAllowedAgencyIds(userRoles, agenciesTree) {
  if (!userRoles || userRoles.length === 0) {
    return []
  }

  let allowedIds = [...userRoles] // Include the role agencies themselves

  for (const roleAgencyId of userRoles) {
    const childIds = getAllChildAgencyIds(roleAgencyId, agenciesTree)
    allowedIds = allowedIds.concat(childIds)
  }

  return [...new Set(allowedIds)] // Remove duplicates
}

/**
 * Finds a vehicle by deviceId or licensePlate
 * @param {string} deviceId - The device ID from SOS
 * @param {string} licensePlate - The license plate from SOS
 * @param {Array} vehicles - Array of vehicles from store
 * @param {Array} vehiclesWithoutAgency - Array of vehicles without agency
 * @returns {Object|null} Vehicle object or null if not found
 */
export function findVehicleByDeviceIdOrPlate(deviceId, licensePlate, vehicles, vehiclesWithoutAgency) {
  const allVehicles = [...vehicles, ...vehiclesWithoutAgency]

  // First try to find by deviceId
  if (deviceId) {
    const vehicleByDevice = allVehicles.find(vehicle => vehicle.deviceId === deviceId)
    if (vehicleByDevice) {
      return vehicleByDevice
    }
  }

  // If not found by deviceId, try by licensePlate
  if (licensePlate) {
    const vehicleByPlate = allVehicles.find(vehicle => vehicle.licensePlate === licensePlate)
    if (vehicleByPlate) {
      return vehicleByPlate
    }
  }

  return null
}

/**
 * Checks if a vehicle is allowed for the current user based on agency permissions
 * Extends the existing vehicle filtering system to include child agencies
 * @param {Object} sosData - SOS alert data containing deviceId and licensePlate
 * @param {Array} userRoles - Array of user role agency IDs
 * @param {number|null} adminRole - Admin role number or null
 * @param {Map} agenciesMap - The agencies map from store
 * @param {Array} agenciesTree - The agencies tree structure
 * @param {Array} vehicles - Array of vehicles from store
 * @param {Array} vehiclesWithoutAgency - Array of vehicles without agency
 * @param {Array} allVehiclesUnfiltered - Array of all vehicles before filtering
 * @returns {boolean} True if vehicle is allowed, false otherwise
 */
export function isVehicleAllowedForUser(sosData, userRoles, adminRole, agenciesMap, agenciesTree, vehicles, vehiclesWithoutAgency, allVehiclesUnfiltered = []) {
  // If user has admin role, allow all SOS alerts
  if (adminRole === 500 || adminRole === 400) {
    console.log('🔓 SOS allowed: User has admin role', adminRole)
    return true
  }

  // Find the vehicle associated with this SOS
  console.log('🔍 Searching for vehicle with deviceId:', sosData.deviceId, 'licensePlate:', sosData.licensePlate)
  console.log('📊 Available vehicles count:', vehicles.length, 'vehiclesWithoutAgency count:', vehiclesWithoutAgency.length, 'allVehiclesUnfiltered count:', allVehiclesUnfiltered.length)

  // First try to find in filtered vehicles (for performance)
  let vehicle = findVehicleByDeviceIdOrPlate(
    sosData.deviceId,
    sosData.licensePlate,
    vehicles,
    vehiclesWithoutAgency
  )

  // If not found in filtered vehicles, search in all unfiltered vehicles
  if (!vehicle && allVehiclesUnfiltered.length > 0) {
    console.log('🔍 Vehicle not found in filtered vehicles, searching in all unfiltered vehicles...')
    vehicle = findVehicleByDeviceIdOrPlate(
      sosData.deviceId,
      sosData.licensePlate,
      allVehiclesUnfiltered,
      []
    )
    if (vehicle) {
      console.log('✅ Vehicle found in unfiltered vehicles:', vehicle.licensePlate, 'agencyId:', vehicle.agencyId)
    }
  }

  if (!vehicle) {
    console.log('🚫 SOS filtered: Vehicle not found for deviceId:', sosData.deviceId, 'licensePlate:', sosData.licensePlate)

    // Debug: Show some sample vehicles to help identify the issue
    const allVehicles = [...vehicles, ...vehiclesWithoutAgency]
    if (allVehicles.length > 0) {
      console.log('📋 Sample vehicles in store (first 3):')
      allVehicles.slice(0, 3).forEach((v, i) => {
        console.log(`   ${i + 1}. deviceId: "${v.deviceId}", licensePlate: "${v.licensePlate}", agencyId: ${v.agencyId}`)
      })

      // Check if there are similar vehicles
      const similarByDevice = allVehicles.filter(v => v.deviceId && v.deviceId.includes('DISP'))
      const similarByPlate = allVehicles.filter(v => v.licensePlate && v.licensePlate.includes('SAT'))

      if (similarByDevice.length > 0) {
        console.log('🔍 Found similar deviceIds:', similarByDevice.map(v => v.deviceId))
      }
      if (similarByPlate.length > 0) {
        console.log('🔍 Found similar licensePlates:', similarByPlate.map(v => v.licensePlate))
      }
    } else {
      console.log('⚠️ No vehicles found in store at all!')
    }

    return false
  }

  // If vehicle has no agency, deny access (unless admin)
  if (!vehicle.agencyId) {
    console.log('🚫 SOS filtered: Vehicle has no agency assigned', vehicle.licensePlate)
    return false
  }

  // First check: Use the same logic as the existing vehicle filtering system
  // Check if the vehicle's agency superParentId matches any of the user's roles
  if (agenciesMap && agenciesMap.get(vehicle.agencyId)) {
    const vehicleAgencyInfo = agenciesMap.get(vehicle.agencyId)
    const vehicleSuperParentId = vehicleAgencyInfo.superParentId

    const allowedBySuperParent = userRoles.some(role => role === vehicleSuperParentId)
    if (allowedBySuperParent) {
      console.log('✅ SOS allowed: Vehicle superParentId', vehicleSuperParentId, 'matches user roles', userRoles)
      return true
    }
  }

  // Second check: Check if vehicle's agency is a child of any user role
  const allowedAgencyIds = getUserAllowedAgencyIds(userRoles, agenciesTree)
  const allowedByChildAgency = allowedAgencyIds.includes(vehicle.agencyId)

  if (allowedByChildAgency) {
    console.log('✅ SOS allowed: Vehicle agency', vehicle.agencyId, 'is child of user roles', userRoles)
    console.log('   Allowed agencies:', allowedAgencyIds)
    return true
  }

  console.log('🚫 SOS filtered: Vehicle agency', vehicle.agencyId, 'not accessible by user roles', userRoles)
  console.log('   Allowed agencies:', allowedAgencyIds)
  return false
}

/**
 * Checks if a SOS alert should be shown to the current user
 * @param {Object} sosData - SOS alert data
 * @param {Object} rootGetters - Vuex root getters
 * @returns {boolean} True if SOS should be shown, false otherwise
 */
export function shouldShowSOSAlert(sosData, rootGetters) {
  try {
    const userRoles = rootGetters['auth/role'] || []
    const adminRole = rootGetters['auth/adminRole']
    const agenciesMap = rootGetters.agenciesMap
    const agenciesTree = rootGetters.agenciesTree || []
    const vehicles = rootGetters.lastPositionsInit || []
    const vehiclesWithoutAgency = rootGetters.vehiclesWithoutAgency || []
    const allVehiclesUnfiltered = rootGetters.allVehiclesUnfiltered || []

    return isVehicleAllowedForUser(
      sosData,
      userRoles,
      adminRole,
      agenciesMap,
      agenciesTree,
      vehicles,
      vehiclesWithoutAgency,
      allVehiclesUnfiltered
    )
  } catch (error) {
    console.error('Error checking SOS permissions:', error)
    // In case of error, deny access for security
    return false
  }
}



// WEBPACK FOOTER //
// ./src/utils/agencyPermissions.js